import { useState, useEffect, useRef, useCallback } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

const IDLE_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
const IDLE_MS = 10 * 60 * 1000; // 10 minutes
const WARNING_BEFORE_MS = 60 * 1000; // Show warning 60s before sign-out

// Per-user idle timeout overrides (milliseconds)
const USER_IDLE_OVERRIDES = {
  "kathir@gmail.com": 2 * 60 * 60 * 1000, // 2 hours
};

export function useIdleTimeout(isAuthenticated, userEmail) {
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const countdownRef = useRef(null);

  const doSignOut = useCallback(async () => {
    setShowWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    try {
      await signOut(auth);
    } catch (_) {
      // ignore
    }
    window.location.replace("/authentication/sign-in");
  }, []);

  const resetTimer = useCallback(() => {
    // Clear all timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setShowWarning(false);

    const timeout = USER_IDLE_OVERRIDES[userEmail] || IDLE_MS;
    const warningAt = Math.max(timeout - WARNING_BEFORE_MS, 0);

    // Warning timer — show dialog before sign-out
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(Math.round(WARNING_BEFORE_MS / 1000));
      countdownRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningAt);

    // Sign-out timer
    timerRef.current = setTimeout(doSignOut, timeout);
  }, [doSignOut, userEmail]);

  const extendSession = useCallback(() => {
    setShowWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setShowWarning(false);
      return;
    }

    IDLE_EVENTS.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      IDLE_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isAuthenticated, resetTimer]);

  return { showWarning, secondsLeft, extendSession, signOutNow: doSignOut };
}

